import { AlertSeverity } from "@app/shared/src/models/Shared.AlertSeverity.gen";
import { AlertStatus } from "@app/shared/src/models/Shared.AlertStatus.gen";
import { AlertType } from "@app/shared/src/models/Shared.AlertType.gen";
import { AlertEventKind } from "@app/shared/src/models/Shared.AlertEventKind.gen";
import { useAuth } from "@app/shared";
import * as alertApi from "@app/shared/src/api/AlertControllerClient.gen";
import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toastError } from "../helpers/toastHelpers";
import { alertTypeLabel } from "../components/pages/notifications/alertDisplay";
import { Pages } from "../components/pages";
import { useAlertOrganizationScope } from "../hooks/useAlertOrganizationScope";
import { useAlertsHub } from "../hooks/useAlertsHub";
import { useAlertsUiVisible } from "../hooks/useAlertsUiVisible";

// Loose shape of the AlertNotificationPayload coming over SignalR. The server
// emits camelCased property names; we keep this type narrow to avoid importing
// every backing model.
export interface AlertPushPayload {
    alertId: string;
    alertType: AlertType;
    severity: AlertSeverity;
    status: AlertStatus;
    eventKind: AlertEventKind;
    deviceId: string;
    deviceName?: string;
    facilityId?: string;
    facilityName?: string;
    message?: string;
    occurredAtUtc: string;
    occurredAtLocal?: string;
    facilityTimezoneId?: string;
    reopenCount?: number;
    deepLink?: string;
}

interface AlertsContextValue {
    active: AlertPushPayload[];
    refresh: () => Promise<void>;
}

const AlertsContext = createContext<AlertsContextValue>({ active: [], refresh: async () => { } });

export const useAlerts = () => useContext(AlertsContext);

// Provider sits inside DashboardLayout so it's only mounted when the user is
// authenticated. On mount it pulls the current set of active alerts and then
// keeps the list current via SignalR. New Critical alerts also surface as a
// toast.
export const AlertsProvider: FC<PropsWithChildren> = ({ children }) => {
    const auth = useAuth();
    // During the internal trial the alert UI is superadmin-only. For everyone
    // else the provider stays inert — no fetch, no SignalR subscription, no
    // toast — so a client is never notified. Gating here (not just hiding the
    // bell) matters because the Critical-alert toast below fires from the hub
    // regardless of whether the bell is rendered.
    const visible = useAlertsUiVisible();
    const userId = visible ? auth.user?.id : undefined;
    const [active, setActive] = useState<AlertPushPayload[]>([]);
    const navigate = useNavigate();

    // Scoped the same way the Alerts page is, from the one shared switch, so the badge always counts
    // exactly the rows the table below it lists. Left unscoped this returned every alert in the fleet
    // for a superadmin (BaseProvider.GetAll hands superadmins unsecured data), so the bell counted
    // other organizations' devices and never matched the page — which is why the scope is shared
    // rather than decided twice.
    const scope = useAlertOrganizationScope();
    const organizationId = visible ? scope.organizationId : undefined;
    // Distinct from "organizationId is set": in all-organizations mode the id is deliberately
    // undefined and the query must still run.
    const canQuery = visible && scope.ready;

    // The SignalR group is joined by the organization the user is IN, not by how wide they've asked the
    // list to be. AlertHub only has per-organization groups, so a superadmin watching the fleet has no
    // group to join for the rest of it — following the current one keeps the live re-read they had
    // before, and the rest arrives on the next refresh. (Per-user AlertChanged pushes are unaffected:
    // those follow subscriptions, not organizations, so a toast still fires wherever the alert is.)
    const watchedOrganizationId = visible ? auth.currentOrganizationId : undefined;

    const refresh = useCallback(async () => {
        if (!canQuery) return;
        const result = await alertApi.list(organizationId);
        if (!result) return;
        setActive(result.map(toPushPayload));
    }, [canQuery, organizationId]);

    useEffect(() => {
        if (!userId) return;
        void refresh();
    }, [userId, refresh]);

    // Follow the alert the toast is about, rather than leaving the user to find it. The alerts page
    // resolves ?alertId= by fetching that one alert by id, so this works even for an alert that is
    // already resolved by the time it's clicked.
    const openAlert = useCallback(
        (alertId: string) => navigate(`${Pages.Alerts.getUrl()}?alertId=${encodeURIComponent(alertId)}`),
        [navigate]
    );

    const onChanged = useCallback((raw: unknown) => {
        const payload = raw as AlertPushPayload;
        if (!payload?.alertId) return;

        setActive((curr) => {
            const idx = curr.findIndex((a) => a.alertId === payload.alertId);
            if (payload.status === AlertStatus.Resolved) {
                return idx >= 0 ? curr.filter((_, i) => i !== idx) : curr;
            }
            if (idx >= 0) {
                const next = curr.slice();
                next[idx] = payload;
                return next;
            }
            return [payload, ...curr];
        });

        // Toast on newly-opened Critical alerts only — Warning and Info live
        // quietly in the bell.
        if (payload.eventKind === AlertEventKind.Opened && payload.severity === AlertSeverity.Critical) {
            // The app's own toast, not react-hot-toast's bare one: it matches every other notification
            // in the dashboard and, more to the point, it can be clicked. alertTypeLabel gives the
            // [Display(Name)] wording ("No Targets Detected"), where the raw enum gave the reader a
            // numeric AlertType.
            const type = alertTypeLabel(payload.alertType);
            const title = payload.deviceName ? `${type} — ${payload.deviceName}` : type;
            toastError(title, payload.message ?? "Click to open this alert.", 8000, () => openAlert(payload.alertId));
        }
    }, [openAlert]);

    // The org signal carries no data on purpose: re-reading is what guarantees the bell agrees with
    // the alerts table, rather than two paths maintaining their own idea of the current state.
    useAlertsHub(userId, watchedOrganizationId, { onAlertChanged: onChanged, onAlertsChanged: refresh });

    return (
        <AlertsContext.Provider value={{ active, refresh }}>
            {children}
        </AlertsContext.Provider>
    );
};

// The REST list and the SignalR push have to land on the SAME shape, because the bell renders
// whichever arrived last. This mapper used to drop deviceName, facilityName and the location's
// timezone, so a live push showed "DIA-130" at 2:53 AM MDT and the very next refresh redrew the same
// row as "92b95c1d" at a browser-local time with no zone on it. Everything the row renders is carried
// here; AlertController.List already Includes the device/location/organization navigations for it.
function toPushPayload(a: any): AlertPushPayload {
    return {
        alertId: a.id ?? "",
        alertType: a.alertType,
        severity: a.severity,
        status: a.status,
        eventKind: AlertEventKind.Opened,
        deviceId: a.deviceId ?? "",
        deviceName: a.deviceName,
        facilityId: a.facilityId,
        facilityName: a.facilityName,
        facilityTimezoneId: a.facilityTimezoneId,
        reopenCount: a.reopenCount,
        message: a.message,
        occurredAtUtc: a.openedAtUtc ?? new Date().toISOString(),
    };
}
